import { useState } from "react"
import { motion } from "motion/react"
import { Calendar, Check, Download01, FileCheck02, RefreshCw02, VideoRecorder } from "@untitledui/icons"
import { toast } from "sonner"
import { activationApi, type ActivationView } from "@/lib/api/onboarding-activation"
import { cx } from "@/lib/utils/cx"
import { downloadSignedPdf } from "./signed-pdf"
import { HairlineRows, PrimaryButton, sectionLabelClass } from "./ui"

// The finale — same activation language as the signing payoff: a deep pine
// stage where the viewfinder locks onto the booked appointment, confetti in
// the brand greens, one shine sweep. Below it, everything practical in one
// aligned stack: the signed copy, add-to-calendar, what happens next.
export function DoneStep({ token, data }: { token: string; data: ActivationView }) {
  const [resetting, setResetting] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const download = async () => {
    if (downloading) return
    setDownloading(true)
    try {
      await downloadSignedPdf(data)
    } catch {
      toast.error("The PDF couldn't be generated — try again.")
    } finally {
      setDownloading(false)
    }
  }

  // DEMO-ONLY affordance: real links are single-use, but the demo token can
  // be re-seeded so the flow is walkable again and again.
  const restartDemo = async () => {
    if (resetting) return
    setResetting(true)
    try {
      await activationApi.resetDemo(token)
      window.location.reload()
    } catch {
      toast.error("Couldn't restart the demo.")
      setResetting(false)
    }
  }

  const b = data.booking
  const bookedDate = b ? new Date(b.date + "T00:00:00") : null
  const whenLong = b
    ? `${bookedDate!.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      })} at ${b.time}`
    : "your scheduled time"

  const d = data.businessDetails
  const systems = [
    ...(d.estimatingSystems ?? []).filter((s) => s !== "Other"),
    ...(d.estimatingOther ? [d.estimatingOther] : []),
  ]
  const itContact = [d.itContactFirstName, d.itContactLastName].filter(Boolean).join(" ")
  const slug = data.shop.legalBusinessName.replace(/[^\w]+/g, "-").replace(/^-+|-+$/g, "")

  const receipts = [
    {
      label: "Signed",
      value: `${data.agreement.title} · ${data.agreement.signerName ?? ""} · v${data.agreement.version}`,
    },
    {
      label: "Connecting",
      value: systems.length
        ? `${systems.join(", ")}${d.bmsSystem ? ` · ${d.bmsSystem}` : ""} — CCi coordinates with ${itContact || "your IT contact"}`
        : `CCi coordinates with ${itContact || "your IT contact"}`,
    },
    { label: "Shop", value: `${data.shop.legalBusinessName} · ${d.city ?? ""}${d.state ? `, ${d.state}` : ""}` },
    ...(d.billingEmail ? [{ label: "Billing", value: `Invoices go to ${d.billingEmail}` }] : []),
  ]

  const next = [
    `${data.salesRep.name} and an implementation specialist join your call — your account is set up together.`,
    `CCi connects ${systems.length ? systems.join(" and ") : "your estimating system"} — nothing gets installed on your side.`,
    "Advisor training and go-live in week one.",
  ]

  const brackets = [
    { cls: "left-0 top-0 border-l-2 border-t-2 rounded-tl-[2px]", x: -22, y: -22 },
    { cls: "right-0 top-0 border-r-2 border-t-2 rounded-tr-[2px]", x: 22, y: -22 },
    { cls: "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-[2px]", x: -22, y: 22 },
    { cls: "bottom-0 right-0 border-b-2 border-r-2 rounded-br-[2px]", x: 22, y: 22 },
  ]

  return (
    <div className="py-2">
      {/* the booked stage */}
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 24 }}
        className="relative overflow-hidden rounded-2xl px-6 py-8 text-center"
        style={{ backgroundColor: "#0B2E22" }}
      >
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -right-16 -top-20 size-[240px] rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-12 size-[220px] rounded-full bg-emerald-400/10 blur-3xl" />
        </div>
        <motion.span
          aria-hidden
          initial={{ x: "-160%" }}
          animate={{ x: "360%" }}
          transition={{ delay: 0.9, duration: 0.8, ease: "easeOut" }}
          className="absolute inset-y-0 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/10 to-transparent"
        />

        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.35 }}
          className="relative flex items-center justify-center gap-2 text-[13px] font-medium text-emerald-300"
        >
          <span className="grid size-[18px] place-items-center rounded-full bg-emerald-400">
            <Check className="size-3 text-[#0B2E22]" strokeWidth={4} aria-hidden />
          </span>
          Onboarding complete — signed and scheduled
        </motion.p>

        {/* the appointment, locked in the viewfinder */}
        <div className="relative mx-auto mt-7 w-fit p-4">
          {brackets.map((br) => (
            <motion.span
              key={br.cls}
              aria-hidden
              initial={{ opacity: 0, x: br.x, y: br.y }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 20, delay: 0.45 }}
              className={cx("absolute size-4 border-emerald-400", br.cls)}
            />
          ))}
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 360, damping: 16, delay: 0.55 }}
            className="relative flex items-center gap-4 rounded-xl bg-white px-5 py-4 text-left shadow-[0_16px_40px_-14px_rgba(0,0,0,0.45)]"
          >
            {bookedDate && (
              <div className="grid w-[58px] shrink-0 overflow-hidden rounded-lg border border-emerald-200 bg-white text-center">
                <span className="bg-emerald-600 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-white">
                  {bookedDate.toLocaleDateString("en-US", { month: "short" })}
                </span>
                <span className="py-1 text-[22px] font-bold leading-none tracking-tight text-[#0C2018]">
                  {bookedDate.getDate()}
                </span>
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[15px] font-bold leading-snug text-[#0C2018]">{whenLong}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-[12px] text-[#5A6A61]">
                <VideoRecorder className="size-3.5 text-emerald-600" aria-hidden />
                45-minute video call · invitation sent to your email
              </p>
            </div>
            {/* confetti from the ticket */}
            {Array.from({ length: 12 }, (_, i) => {
              const angle = (i / 12) * Math.PI * 2
              const dist = 70 + (i % 3) * 26
              return (
                <motion.span
                  key={i}
                  aria-hidden
                  initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                  animate={{
                    x: Math.cos(angle) * dist,
                    y: Math.sin(angle) * dist + 10,
                    scale: 1,
                    opacity: 0,
                    rotate: 120 + i * 30,
                  }}
                  transition={{ duration: 0.85 + (i % 4) * 0.1, ease: "easeOut", delay: 0.75 }}
                  className="absolute left-1/2 top-1/2 size-1.5 rounded-[2px]"
                  style={{ background: ["#34D399", "#6EE7B7", "#A7F3D0", "#0FA36B"][i % 4] }}
                />
              )
            })}
          </motion.div>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.45 }}
          className="relative mt-6 text-[23px] font-bold leading-tight text-white"
        >
          You're booked in.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.4 }}
          className="relative mt-1 text-[13.5px] text-white/60"
        >
          Nothing else is needed from you before the call.
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.5 }}
          className="relative mt-3 text-[8.5px] font-semibold uppercase tracking-[0.3em] text-emerald-300"
        >
          Collision · AI · Intelligence
        </motion.p>
      </motion.div>

      {/* the signed copy — same receipt row as the signing step */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.45, duration: 0.4 }}
        className="mt-4 flex flex-col gap-3 rounded-xl border border-[#E5EAE7] bg-white px-4 py-3.5 sm:flex-row sm:items-center"
      >
        <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-emerald-50">
          <FileCheck02 className="size-5 text-emerald-600" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13.5px] font-semibold text-[#0C2018]">
            eTX-Data-Authorization-{slug}.pdf
          </p>
          <p className="text-[12px] text-[#96A29A]">Your signed copy — keep it with your records</p>
        </div>
        <button
          type="button"
          onClick={download}
          disabled={downloading}
          className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-[#D5DDD8] bg-white px-4 text-[13.5px] font-semibold text-[#0C2018] transition-colors hover:bg-[#F4F6F5] disabled:opacity-50"
        >
          <Download01 className="size-4 text-emerald-600" aria-hidden />
          {downloading ? "Preparing…" : "Download"}
        </button>
      </motion.div>

      {/* one full-width action, flush with the stack */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.4 }}
        className="mt-4"
      >
        <PrimaryButton
          onClick={() => toast.success("Calendar invite sent to your email")}
          className="w-full"
        >
          <Calendar className="size-4" />
          Add to calendar
        </PrimaryButton>
      </motion.div>

      {/* what happens next */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.75, duration: 0.4 }}
      >
        <div className="mt-7">
          <p className={cx("mb-2", sectionLabelClass)}>What happens next</p>
          <ol className="space-y-1.5">
            {next.map((line, i) => (
              <li key={i} className="flex gap-3.5 text-[14px] leading-relaxed text-[#4A5A51]">
                <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-emerald-600 text-[11px] font-bold text-white">
                  {i + 1}
                </span>
                {line}
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-6">
          <HairlineRows>
            {receipts.map((r) => (
              <div
                key={r.label}
                className="flex flex-col gap-0.5 py-2 sm:flex-row sm:items-baseline sm:gap-6"
              >
                <span className="w-28 shrink-0 text-[12px] font-medium text-[#96A29A]">{r.label}</span>
                <span className="min-w-0 text-[14px] font-medium leading-relaxed text-[#33413A]">
                  {r.value}
                </span>
              </div>
            ))}
          </HairlineRows>
        </div>

        <p className="mt-5 text-[13px] text-[#8A9890]">
          Questions before then? {data.salesRep.name}
          {data.salesRep.phone ? ` · ${data.salesRep.phone}` : ""} · {data.salesRep.email}
        </p>

        {token === "demo" && (
          <button
            onClick={restartDemo}
            disabled={resetting}
            className="mt-5 flex items-center gap-2 rounded-full border border-dashed border-[#C6D2CB] px-4 py-2 text-[12.5px] font-medium text-[#8A9890] transition-colors hover:border-emerald-300 hover:text-emerald-700 disabled:opacity-50"
          >
            <RefreshCw02 className={resetting ? "size-3.5 animate-spin" : "size-3.5"} />
            Restart demo from the beginning
          </button>
        )}
      </motion.div>
    </div>
  )
}
